import moment from 'moment-timezone';

/*
 * Did the team DISAGREE, or did the answer CHANGE over time?
 *
 * When staff gave different answers, it's either a real disagreement (the versions were
 * in use at the same time) or a change (one version stopped, the next started: a price
 * rise, a new meeting point, cash -> pay-by-link). Treating a change as a disagreement
 * invites the operator to pick the old answer, so the two are told apart by when each
 * version was sent. The backend should compute the same thing (see the plan).
 *
 * Returns { state, newestIndex }:
 *   'none'    no team answers        'agree'  one version
 *   'changed' versions follow one another in time (newestIndex = the current one)
 *   'differ'  versions overlapped, or we can't tell (no dates)
 */
// Versions a couple of days apart still count as "one after the other".
const CHANGE_TOLERANCE_DAYS = 2;

export function teamTimeline(answers = []) {
  if (!answers.length) return { state: 'none', newestIndex: null };
  if (answers.length === 1) return { state: 'agree', newestIndex: 0 };
  const dated = answers.every((a) => a.first_ts && a.last_ts);
  if (!dated) return { state: 'differ', newestIndex: null };
  const order = answers.map((a, i) => i).sort((i, j) => moment(answers[i].first_ts).diff(answers[j].first_ts));
  const sequential = order.every((idx, k) => k === 0
    || moment(answers[order[k - 1]].last_ts).isSameOrBefore(moment(answers[idx].first_ts).add(CHANGE_TOLERANCE_DAYS, 'days')));
  return sequential
    ? { state: 'changed', newestIndex: order[order.length - 1], order }
    : { state: 'differ', newestIndex: null };
}

export const teamDiffers = (item) => teamTimeline(item.team_answers).state === 'differ';

// The team answer to pre-select, or null to have the team pick one:
//  - one answer: that one;
//  - changed over time: the newest, once it has been sent CHANGE_SETTLED times or is clearly the
//    most sent. A reply sent once or twice after another is usually a one-off (a closure that
//    day, one guest's time), and that is what a quick "Train" would otherwise teach the bot;
//  - disagreeing: the clearly most sent one.
// Scored on prod 2026-10-05 against the old rule (newest when changed, else the first): on cards
// already trained it pre-selected a reply staff then rejected 1 time in 7 instead of 4, and it
// stopped pre-selecting 17 of the 20 one-off replies a manual review flagged.
const CHANGE_SETTLED = 3;

export const defaultTeamAnswerIndex = (answers = []) => {
  const t = teamTimeline(answers);
  if (t.state === 'none') return null;
  if (t.state === 'agree') return 0;
  const counts = answers.map((a) => a.count || 1);
  const most = Math.max(...counts);
  const clearlyMost = (i) => counts[i] === most && counts.filter((c) => c === most).length === 1;
  if (t.state === 'changed') {
    return counts[t.newestIndex] >= CHANGE_SETTLED || clearlyMost(t.newestIndex) ? t.newestIndex : null;
  }
  const top = counts.indexOf(most);
  return clearlyMost(top) ? top : null;
};

const short = (text, n = 60) => (text.length > n ? `${text.slice(0, n).trimEnd()}…` : text);
const day = (ts) => moment(ts).format('MMM D');

// One line for the card: how the team's answers relate, in plain words.
export function timelineMessage(answers = []) {
  const t = teamTimeline(answers);
  if (t.state === 'changed') {
    const preselected = defaultTeamAnswerIndex(answers) !== null;
    const tail = preselected
      ? 'The newer answer is pre-selected.'
      : 'Pick the one that’s right now, or write your own.';
    if (answers.length === 2) {
      const [oldA, newA] = t.order.map((i) => answers[i]);
      return {
        severity: 'info',
        text: `Your team said “${short(oldA.text)}” until ${day(oldA.last_ts)}, and “${short(newA.text)}” since ${day(newA.first_ts)}. ${tail}`,
      };
    }
    return {
      severity: 'info',
      text: `Your team’s answer changed over time. ${preselected ? 'The newest one is pre-selected.' : tail}`,
    };
  }
  if (t.state === 'differ') {
    let who = `Your team gave ${answers.length} different answers at the same time.`;
    if (allFromWebsite(answers)) who = `Your website gives ${answers.length} different answers.`;
    else if (answers.some(fromWebsite)) who = `Your team and your website give ${answers.length} different answers.`;
    return { severity: 'warning', text: `${who} Pick the right one, or write your own.` };
  }
  return null;
}

// Versions read off the operator's own website rather than sent by staff (cards we seed when a site
// contradicts itself): { source: 'website', pages, count: 1, no dates }. Hannah 2026-10-08 read
// "Sent once by guidedtour-spain.com" as a staff user she couldn't find.
export const fromWebsite = (a) => a?.source === 'website';
export const allFromWebsite = (answers = []) => answers.length > 0 && answers.every(fromWebsite);

export const answerDateRange = (a) => (a.first_ts && a.last_ts
  ? `${day(a.first_ts)} – ${day(a.last_ts)}`
  : null);
